import { Button, createStyles, makeStyles, TextField, Theme, Typography } from "@material-ui/core";
import { useContext, useState } from "react";
import { RootContext } from "../../../stores";
import { Alert, AlertTitle } from "@material-ui/lab";
import { IOrganizationPrimaryDetailsFormValues } from "../_interfaces/IOrganizationPrimaryDetailsFormValues";
import React from "react";
import TextFieldWithCharacterLimit from "../../../components/Shared/TextFieldWithCharacterLimit";
import FormLoadingSpinner from "../../../components/Organization/VolunteerOpportunities/FormLoadingSpinner";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        form: {
            position: 'relative'
        },
        alertFlex: {
            display: 'flex',
            justifyContent: 'center'
        },
        alert: {
            marginBottom: theme.spacing(2),
            boxSizing: 'border-box',
            maxWidth: '500px',
            textAlign: 'left',
            flexGrow: 1
        },
        textField: {
            margin: theme.spacing(1),
            width: '40ch',
            [theme.breakpoints.down('xs')]: {
                flexGrow: 1,
                width: 'auto'
            }
        },
        title: {
            color: theme.palette.action.active,
            marginBottom: theme.spacing(3)
        },
        flex: {
            display: 'flex',
            flexDirection: 'column',
            [theme.breakpoints.up('sm')]: {
                alignItems: 'center'
            }
        },
        buttons: {
            display: 'flex',
            flexGrow: 1,
            justifyContent: 'space-evenly',
            maxWidth: '400px'
        },
        button: {
            marginTop: theme.spacing(2),
        },
        link: {
            color: theme.palette.primary.main,
            '&:hover': {
                textDecoration: 'underline',
                cursor: 'pointer'
            }
        },
    }),
);

interface OrganizationPrimaryDetailsFormProps {
    values: IOrganizationPrimaryDetailsFormValues;
    onChange: (patch: Partial<IOrganizationPrimaryDetailsFormValues>) => void;
    onSubmit: (override?: Partial<IOrganizationPrimaryDetailsFormValues>) => void;
    isSubmitting?: boolean;
    error?: string;
    fieldErrors?: Partial<Record<keyof IOrganizationPrimaryDetailsFormValues, string>>;
}

export const OrganizationPrimaryDetailsForm: React.FC<OrganizationPrimaryDetailsFormProps> = ({
    values,
    onChange,
    onSubmit,
    isSubmitting,
    error,
    fieldErrors
}) => {

    /********** State **********/

    const getFormId = () => {
        return `form-main-organization-details-${Date.now()}-${Math.random()}`;
    }

    const [formId] = useState(() => getFormId());

    /********** React hooks **********/

    const classes = useStyles();
    const rootStore = useContext(RootContext);

    /********** Event handlers **********/

    const onContinue = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault(); // Prevent page reload
        event.stopPropagation();
        onSubmit();
    }

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange({ organizationName: event.target.value });
    }

    const handleEINChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange({ ein: event.target.value });
    }

    const handleSignUpCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange({ signUpCode: event.target.value });
    }

    const handleSignUpCodeRequest = () => {
        rootStore.navigationStore.appwideDialogStates.contactUsDialog.setOpen(true);
        rootStore.helpStore.setHelpMessageOverrides({
            subject: 'Sign Up Code Request',
        });
    };

    const nameError = fieldErrors?.organizationName || "";
    const einError = fieldErrors?.ein || "";
    const signUpCodeError = fieldErrors?.signUpCode || "";

    /********** Render **********/

    return (
        <form onSubmit={onContinue} noValidate autoComplete="off" id={formId} className={classes.form}>
            {values.loading ? <FormLoadingSpinner /> : undefined}
            {error
                ? <div className={classes.flex}>
                    <Alert severity="error" className={classes.alertFlex}>
                        <AlertTitle>Registration Error</AlertTitle>
                        {error}
                    </Alert>
                </div>
                : null
            }
            <div>
                <Typography variant="h5" className={classes.title}>Enter your organization's details to get started.</Typography> {/* // TODO: Adjust wording after demo */}
                <div className={classes.flex}>
                    <TextFieldWithCharacterLimit
                        characterLimit={150}
                        TextFieldProps={{
                            id: "standard-name-input",
                            label: "Organization Name",
                            variant: "outlined",
                            required: true,
                            value: values.organizationName,
                            onChange: handleNameChange,
                            autoFocus: true,
                            className: classes.textField,
                            error: nameError.length > 0,
                            helperText: nameError.length > 0 ? nameError : undefined
                        }}
                    />
                    {values.isForProfit
                        ? null
                        : <TextField
                            id="standard-ein-input"
                            label="EIN"
                            variant="outlined"
                            required
                            value={values.ein}
                            onChange={handleEINChange}
                            className={classes.textField}
                            error={einError.length > 0}
                            helperText={einError.length > 0 ? einError : undefined}
                            inputProps={{ maxLength: 9 }}
                        />
                    }
                    <TextField
                        id="sign-up-code"
                        label="Sign Up Code"
                        variant="outlined"
                        required
                        value={values.signUpCode}
                        onChange={handleSignUpCodeChange}
                        className={classes.textField}
                        error={signUpCodeError.length > 0}
                        helperText={signUpCodeError.length > 0
                            ? signUpCodeError
                            : <Typography variant="caption">
                                Contact us to <span onClick={handleSignUpCodeRequest} className={classes.link}>request a sign up code</span> if you don't already have one.
                            </Typography>}
                    />
                </div>
            </div>
            <div className={classes.flex}>
                <div className={classes.buttons}>
                    <Button
                        color="primary"
                        variant="contained"
                        type="submit"
                        className={classes.button}
                        disabled={isSubmitting}
                    // disabled={!currentRegistrationStep.admissable || currentRegistrationStep.state.loading}
                    >
                        Continue
                    </Button>
                </div>
            </div>
        </form>
    );
};
